import React from 'react';
import { useScreenClass } from '@vp/swan';
import {
    DrawerRevisionRequest,
    EmptyCanvas,
    ErrorLoadingState,
    RevisionRequestPanelData,
    SingleImageDesignPreview,
    useCanvasProps,
    useCollaborationContext,
    useVersion,
} from '@99designs/collaborate-views';

export interface RevisionCanvasProps {
    revisionRequestId: number | null;
    deliveryId: string;
    subRoute: string | null;
    interactable?: boolean;
    revisionRequest: RevisionRequestPanelData;
    handleUpdate: ({ comment }: { comment?: string | undefined }) => void;
}

export const RevisionCanvas: React.FC<RevisionCanvasProps> = ({
    deliveryId,
    revisionRequestId,
    subRoute,
    interactable = false,
    revisionRequest,
    handleUpdate,
}) => {
    const {
        collaborationId,
        revisionRequests,
        deliveries,
        trackingProperties,
        viewerRole,
        locale,
        hasDesignLiveDelivery,
    } = useCollaborationContext();
    const { loading, errors, props } = useCanvasProps(
        deliveryId,
        collaborationId,
        revisionRequestId,
        deliveries.length > 0,
        trackingProperties,
        locale
    );
    const { version, setVersion } = useVersion(subRoute);
    const screenClass = useScreenClass();
    const isDevEnv =
        window.location.hostname === 'localhost' ||
        window.location.hostname.includes('staging.vistaprint.com');

    return (
        <ErrorLoadingState loading={loading} errors={errors} data={props}>
            {(props) =>
                Object.keys(props.images).length === 0 ? (
                    <EmptyCanvas isDesignLive={hasDesignLiveDelivery} />
                ) : (
                    <>
                        <SingleImageDesignPreview
                            {...props}
                            revisionsRemaining={revisionRequests.remaining}
                            revisionsTotal={revisionRequests.used}
                            version={version}
                            setVersion={setVersion}
                            isDesignerView={viewerRole === 'VIEWER_ROLE_DESIGNER'}
                            interactable={interactable}
                        />
                        {/* Only set drawer for when Requesting revision. Read-only view is WIP */}
                        {interactable && screenClass === 'xs' && isDevEnv && (
                            <DrawerRevisionRequest
                                revisionRequest={revisionRequest}
                                handleUpdate={handleUpdate}
                                deliveryVersionId={deliveryId}
                                collaborationId={collaborationId}
                                images={props.images}
                                pins={props.pins}
                            />
                        )}
                    </>
                )
            }
        </ErrorLoadingState>
    );
};
